// frontend/src/components/planner/AddTransferModal.jsx
import React, { useState, useEffect } from 'react';
import {
  X,
  ArrowRight,
  Building2,
  DollarSign,
  Wallet
} from 'lucide-react';
import api from '../../utils/api';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';

const TRANSFER_TYPES = [
  { value: 'salary', label: 'Salary / Wages' },
  { value: 'distribution', label: 'Owner Distribution' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'loan_payment', label: 'Loan Payment' },
  { value: 'other', label: 'Other' }
];

const FREQUENCIES = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 Weeks' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

function getTodayString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AddTransferModal({ onClose, onSuccess }) {
  const { activeFilter } = useBusinessEntity();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [entities, setEntities] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transferMode, setTransferMode] = useState('account');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    from_entity_id: '',
    to_entity_id: '',
    from_bank_account_id: '',
    to_bank_account_id: '',
    amount: '',
    frequency: 'weekly',
    next_due_date: getTodayString(),
    transfer_type: 'other'
  });

  useEffect(() => {
    async function loadData() {
      try {
        const [entitiesRes, accountsRes] = await Promise.all([
          api.get('/api/business-entities'),
          api.get('/api/planner/accounts')
        ]);
        const loadedEntities = entitiesRes.data.businessEntities || [];
        setEntities(loadedEntities);
        setAccounts(accountsRes.data.accounts || []);
        // Default to account mode; switch to entity if entities exist but user can toggle.
        // Prefer the active entity filter (if numeric and present); fall back to first entity.
        if (loadedEntities.length > 0) {
          const asNum = parseInt(activeFilter, 10);
          const preferredId = Number.isFinite(asNum) && loadedEntities.some(e => e.id === asNum)
            ? asNum
            : loadedEntities[0].id;
          setFormData(prev => ({ ...prev, from_entity_id: preferredId }));
        }
      } catch (err) {
        console.error('Error loading transfer data:', err);
      }
    }
    loadData();
    // activeFilter intentionally read once on mount; modal is transient and the
    // shell pills live outside it, so it can't change mid-session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toEntityOptions = entities.filter(e => e.id !== parseInt(formData.from_entity_id, 10));
  const toAccountOptions = accounts.filter(a => String(a.id) !== String(formData.from_bank_account_id));

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) { setError('Transfer name is required'); return; }
    if (formData.name.trim().length > 200) { setError('Name must be 200 characters or less'); return; }
    if (!formData.amount || parseFloat(formData.amount) <= 0) { setError('Amount must be greater than zero'); return; }
    if (parseFloat(formData.amount) > 1_000_000) { setError('Amount cannot exceed $1,000,000'); return; }
    if (!formData.next_due_date) { setError('Next due date is required'); return; }

    if (transferMode === 'entity' && !formData.from_entity_id) { setError('Source entity is required'); return; }
    if (transferMode === 'account' && !formData.from_bank_account_id) { setError('Source account is required'); return; }
    if (transferMode === 'account' && !formData.to_bank_account_id) { setError('Destination account is required'); return; }

    const payload = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      amount: parseFloat(formData.amount),
      frequency: formData.frequency,
      next_due_date: formData.next_due_date,
      transfer_type: formData.transfer_type
    };

    if (transferMode === 'entity') {
      payload.from_entity_id = parseInt(formData.from_entity_id, 10);
      payload.to_entity_id = formData.to_entity_id ? parseInt(formData.to_entity_id, 10) : null;
    } else {
      payload.from_bank_account_id = parseInt(formData.from_bank_account_id, 10);
      payload.to_bank_account_id = parseInt(formData.to_bank_account_id, 10);
    }

    try {
      setSaving(true);
      await api.post('/api/transfers', payload);
      onSuccess();
    } catch (err) {
      console.error('Error creating transfer:', err);
      setError(err.response?.data?.error || 'Failed to create transfer');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-[10vh] px-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New Transfer</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Info */}
        <div className="px-5 pt-4">
          <p className="text-sm text-gray-500">
            Creates a paired bill (outflow) and income (inflow) that net to zero in the combined view.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Mode Toggle */}
          {entities.length > 0 && (
            <div className="flex items-center gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => setTransferMode('account')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  transferMode === 'account' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Wallet className="w-3.5 h-3.5" />
                Between Accounts
              </button>
              <button
                type="button"
                onClick={() => setTransferMode('entity')}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  transferMode === 'entity' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                Between Entities
              </button>
            </div>
          )}

          {/* Name + Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={transferMode === 'account' ? 'e.g. Weekly GFOB to Joint' : 'e.g. Owner Salary'}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.transfer_type}
                onChange={e => setFormData(prev => ({ ...prev, transfer_type: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {TRANSFER_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* From → To (Account mode) */}
          {transferMode === 'account' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Wallet className="w-3.5 h-3.5 inline mr-1" />
                  From Account *
                </label>
                <select
                  value={formData.from_bank_account_id}
                  onChange={e => setFormData(prev => ({ ...prev, from_bank_account_id: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                >
                  <option value="">Select source account...</option>
                  {accounts.map(a => (
                    <option key={a.id} value={a.id}>{a.displayNameWithInstitution || a.displayName}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-center">
                <ArrowRight className="w-4 h-4 text-gray-400 rotate-90" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Wallet className="w-3.5 h-3.5 inline mr-1" />
                  To Account *
                </label>
                <select
                  value={formData.to_bank_account_id}
                  onChange={e => setFormData(prev => ({ ...prev, to_bank_account_id: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                >
                  <option value="">Select destination account...</option>
                  {toAccountOptions.map(a => (
                    <option key={a.id} value={a.id}>{a.displayNameWithInstitution || a.displayName}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* From → To (Entity mode) */}
          {transferMode === 'entity' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="w-3.5 h-3.5 inline mr-1" />
                  From Entity *
                </label>
                <select
                  value={formData.from_entity_id}
                  onChange={e => setFormData(prev => ({ ...prev, from_entity_id: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                >
                  <option value="">Select source entity...</option>
                  {entities.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex justify-center">
                <ArrowRight className="w-4 h-4 text-gray-400 rotate-90" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Building2 className="w-3.5 h-3.5 inline mr-1" />
                  To (Destination)
                </label>
                <select
                  value={formData.to_entity_id}
                  onChange={e => setFormData(prev => ({ ...prev, to_entity_id: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Personal</option>
                  {toEntityOptions.map(e => (
                    <option key={e.id} value={e.id}>{e.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Leave as "Personal" for salary/distributions to yourself</p>
              </div>
            </div>
          )}

          {/* Amount + Frequency + Date */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <DollarSign className="w-3.5 h-3.5 inline mr-1" />
                Amount *
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={formData.amount}
                onChange={e => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                placeholder="500.00"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
              <select
                value={formData.frequency}
                onChange={e => setFormData(prev => ({ ...prev, frequency: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Next Due *</label>
              <input
                type="date"
                value={formData.next_due_date}
                onChange={e => setFormData(prev => ({ ...prev, next_due_date: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Optional notes"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium"
            >
              {saving ? 'Creating...' : 'Create Transfer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
